import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "add-project-team-member-form" }
const _hoisted_2 = {
  key: 0,
  class: "selected-employee-details"
}
const _hoisted_3 = { class: "readonly-field" }
const _hoisted_4 = { class: "readonly-field-label" }
const _hoisted_5 = { class: "readonly-field-value" }
const _hoisted_6 = {
  key: 0,
  class: "loading apollo"
}

import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";
import { useMutation, useQuery } from "@vue/apollo-composable";
import { logErrorMessages } from "@vue/apollo-util";

import Modal from "@/components/Modal.vue";
import FormItem from "@/components/form/FormItem.vue";
import BasicButton from "@/components/BasicButton.vue";

import { ADD_PROJECT_TEAM_MEMBER } from "@/graphql/project/mutations";
import { GET_PROJECT_TEAM } from "@/graphql/project/queries";
import { GET_DETAILED_EMPLOYEES } from "@/graphql/user/queries";

import enUK from "@/locales/project/en-UK.json";
import { TeamMember } from "@/helpers/project/typesDefinition";
import { User } from "@/helpers/user/typesDefinition";

interface EmployeeOption {
  id: string;
  name: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AddProjectTeamMember',
  props: {
  projectId: {
    type: String,
    required: true,
  },
  currentTeamMembers: {
    type: Array as () => TeamMember[],
    required: true,
  },
},
  emits: ["close", "added"],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;

const { t } = useI18n({
  useScope: "global",
  inheritLocale: true,
  messages: {
    "en-UK": enUK,
  },
});

const toast = useToast();

const {
  result: employeesResult,
  loading: loadingEmployees,
  onError: onEmployeesError,
} = useQuery(GET_DETAILED_EMPLOYEES);

onEmployeesError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.getEmployees"));
});

const selectedEmployeeId = ref("");
const roleName = ref("");
const hourlyRate = ref<number | string>("");

const currentTeamMemberUserIds = computed(() => {
  return props.currentTeamMembers.map((teamMember) => teamMember.user.id);
});

const employees = computed<User[]>(() => {
  return employeesResult.value?.users ?? [];
});

const availableEmployees = computed<User[]>(() => {
  return employees.value.filter((employee) => {
    return !currentTeamMemberUserIds.value.includes(employee.id);
  });
});

const employeeOptions = computed<EmployeeOption[]>(() => {
  return availableEmployees.value.map((employee) => ({
    id: employee.id,
    name: [employee.firstName, employee.lastName].filter(Boolean).join(" "),
  }));
});

const selectedEmployee = computed<User | undefined>(() => {
  return availableEmployees.value.find((employee) => {
    return employee.id === selectedEmployeeId.value;
  });
});

const hasSelectedEmployee = computed(() => {
  return Boolean(selectedEmployee.value);
});

watch(selectedEmployee, (employee) => {
  if (!employee) {
    roleName.value = "";
    hourlyRate.value = "";
    return;
  }

  roleName.value = employee.defaultRole?.name ?? "";
  hourlyRate.value = employee.defaultRole?.hourlyRate
    ? employee.defaultRole.hourlyRate / 100
    : "";
});

const {
  mutate: addProjectTeamMember,
  onError: onAddProjectTeamMemberError,
  onDone: onAddProjectTeamMemberDone,
  loading: addingProjectTeamMember,
} = useMutation(ADD_PROJECT_TEAM_MEMBER, {
  refetchQueries: [
    {
      query: GET_PROJECT_TEAM,
      variables: {
        id: props.projectId,
      },
    },
  ],
});

onAddProjectTeamMemberError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.addTeamMember"));
});

onAddProjectTeamMemberDone(() => {
  toast.success(t("success.addTeamMember"));
  emit("added");
  emit("close");
});

const isLoading = computed(() => {
  return loadingEmployees.value || addingProjectTeamMember.value;
});

async function handleAddProjectTeamMember(): Promise<void> {
  if (!selectedEmployeeId.value) {
    toast.error(t("errors.requiredFields"));
    return;
  }

  if (!selectedEmployee.value?.defaultRole) {
    toast.error(t("errors.employeeMissingDefaultRole"));
    return;
  }

  if (hourlyRate.value === "" || hourlyRate.value === null) {
    toast.error(t("errors.requiredFields"));
    return;
  }

  const hourlyRateInCents =
    typeof hourlyRate.value === "string"
      ? Math.round(parseFloat(hourlyRate.value) * 100)
      : Math.round(hourlyRate.value * 100);

  if (isNaN(hourlyRateInCents)) {
    toast.error(t("errors.invalidHourlyRate"));
    return;
  }

  await addProjectTeamMember({
    input: {
      projectId: props.projectId,
      userId: selectedEmployeeId.value,
      hourlyRate: hourlyRateInCents,
    },
  });
}

function closeModal(): void {
  emit("close");
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Modal, { onClose: closeModal }, {
    header: _withCtx(() => [
      _createTextVNode(_toDisplayString(_unref(t)("titles.addTeamMember")), 1)
    ]),
    body: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(FormItem, {
          input: selectedEmployeeId.value,
          "onUpdate:input": _cache[0] || (_cache[0] = ($event: any) => ((selectedEmployeeId).value = $event)),
          label: _unref(t)('labels.employee'),
          name: "employee",
          "input-type": "select",
          options: employeeOptions.value
        }, null, 8, ["input", "label", "options"]),
        (hasSelectedEmployee.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, [
                _createElementVNode("div", _hoisted_4, _toDisplayString(_unref(t)("labels.role")), 1),
                _createElementVNode("div", _hoisted_5, _toDisplayString(roleName.value), 1)
              ]),
              _createVNode(FormItem, {
                input: hourlyRate.value,
                "onUpdate:input": _cache[1] || (_cache[1] = ($event: any) => ((hourlyRate).value = $event)),
                label: _unref(t)('labels.hourlyRate'),
                name: "hourlyRate",
                "input-type": "basic"
              }, null, 8, ["input", "label"])
            ]))
          : _createCommentVNode("", true)
      ]),
      (isLoading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(_unref(t)("loading")), 1))
        : _createCommentVNode("", true)
    ]),
    footer: _withCtx(() => [
      _createVNode(BasicButton, {
        class: "add-button",
        type: "submit",
        disabled: isLoading.value || !hasSelectedEmployee.value,
        onClick: handleAddProjectTeamMember
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(t)("buttons.addTeamMember")), 1)
        ]),
        _: 1
      }, 8, ["disabled"]),
      _createVNode(BasicButton, {
        class: "cancel-button",
        onClick: closeModal
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(t)("buttons.cancel")), 1)
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})